import React, { useState, useRef } from 'react';
import { 
  Container, Card, CardContent, Typography, TextField, Button, Box, 
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, IconButton, FormControlLabel, Checkbox, FormGroup
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import LoadListTable from './LoadListTable';
import { API_URL } from '../../config';

const LoadListViewV2 = ({ onBackClick }) => {
  const [container, setContainer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const printRef = useRef();

  // Column visibility — PALLET NO., QUALITY, SPECIES, GLUE, CLASS, TYPE, CERTIFICATES
  const [columnVisibility, setColumnVisibility] = useState({
    palletNo: true,
    quality: true,
    species: true,
    glue: false,
    class: false,
    type: false,
    certificates: true,
  });

  const handleColumnToggle = (col) => {
    setColumnVisibility(prev => ({ ...prev, [col]: !prev[col] }));
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: ' ',
    pageStyle: `
      @page { size: A4; margin: 3mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; margin: 0 !important; padding: 0 !important; }
        .MuiContainer-root { padding: 0 !important; margin: 0 !important; }
        .print-only { page-break-after: auto !important; break-after: auto !important; }
        .print-only > * { page-break-after: auto !important; break-after: auto !important; }
      }
    `
  });

  const handleExcelDownload = () => {
    if (!data || !data.mainRows.length) return;

    const rows = getLoadListData(data.mainRows);
    const excelData = rows.map(row => {
      const obj = { 'SIZE AND THICKNESS': `${row.length} X ${row.width} X ${row.thickness} MM` };
      if (columnVisibility.palletNo) obj['PALLET NO.'] = '';
      obj['NO. OF PALLETS'] = row.palletNumber;
      obj['PCS. PER PALLET'] = row.pcs;
      if (columnVisibility.quality)      obj['QUALITY']      = row.quality || '';
      if (columnVisibility.species)      obj['SPECIES']      = row.species || '';
      if (columnVisibility.glue)         obj['GLUE']         = row.glue || '';
      if (columnVisibility.class)        obj['CLASS']        = row.class || '';
      if (columnVisibility.type)         obj['TYPE']         = row.type || '';
      if (columnVisibility.certificates) obj['CERTIFICATES'] = row.certificates || '';
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Load List');
    XLSX.writeFile(workbook, `Load_List_${container}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await fetch(`${API_URL}/container/viewpackinglistv2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ container })
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();

      const companyInfo    = apiData.data?.[0]?.[0] || {};
      const workOrderInfo  = apiData.data?.[1]?.[0] || {};
      const mainRows       = apiData.data?.[2] || [];
      const containerInfo  = apiData.data?.[3]?.[0] || {};
      const certificatesRow = apiData.data?.[4]?.[0] || {};

      if (mainRows.length === 0) {
        setError('No records found for this container.');
        return;
      }

      setData({
        companyInfo,
        workOrderInfo,
        mainRows,
        containerInfo,
        certificates: [
          certificatesRow.CERTIFICATE1,
          certificatesRow.CERTIFICATE2,
          certificatesRow.CERTIFICATE3,
          certificatesRow.CERTIFICATE4,
          certificatesRow.CERTIFICATE5
        ].filter(Boolean)
      });
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  const getLoadListData = (rows) =>
    rows.map(row => ({
      length:       row.LENGTH,
      width:        row.WIDTH,
      thickness:    row.THICKNESS,
      pcs:          row.PCS,
      quality:      row.QUALITY,
      species:      row.SPECIES,
      glue:         row.GLUE,
      class:        row.CLASS,
      type:         row.TYPE,
      certificates: row.CERTIFICATES,
      palletNumber: row.LOTNUMBER || 0,
    })).sort((a, b) => a.palletNumber - b.palletNumber);

  const isFSC = (data?.workOrderInfo?.CERTIFICATION_LEGAL || '').replace(/non-fsc/gi, '').toUpperCase().includes('FSC');
  const showCertLine = isFSC && !columnVisibility.certificates;

  // Shared cell style
  const hdrCell = {
    fontWeight: 700,
    textAlign: 'center',
    fontSize: '0.78rem',
    py: 0.8,
    px: 0.5,
    border: '1px solid #e0e0e0',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'normal',
  };
  const dataCell = {
    textAlign: 'center',
    fontSize: '0.78rem',
    py: 0.5,
    px: 0.8,
    border: '1px solid #e0e0e0',
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* Search Card */}
      <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', background: 'white', border: '1px solid #e0e0e0' }} className="print-hide">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: '#1b4332', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <SearchIcon sx={{ fontSize: 28, color: '#1b4332', mr: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1b4332', letterSpacing: '-0.5px' }}>
              Load List Container Search (V2)
            </Typography>
          </Box>
          <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="stretch">
            <TextField
              label="Enter Container Number"
              variant="outlined"
              value={container}
              onChange={e => setContainer(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && !loading && container && handleSearch()}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1, backgroundColor: '#fafafa',
                  '&:hover': { backgroundColor: '#fff' },
                  '& fieldset': { borderColor: '#e0e0e0' },
                  '&:hover fieldset': { borderColor: '#1b4332' },
                  '&.Mui-focused fieldset': { borderColor: '#1b4332', borderWidth: '1px' },
                },
                '& .MuiInputLabel-root': { color: '#666' },
              }}
              InputProps={{ style: { fontSize: '1rem', padding: '4px' } }}
            />
            <Button
              variant="contained" size="large" startIcon={<SearchIcon />}
              disabled={loading || !container} onClick={handleSearch}
              sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#1b4332', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#0f2419', boxShadow: 'none' } }}
            >
              Search
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results Header */}
      {data && (
        <Card sx={{ mb: 3, borderRadius: 2, background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }} className="print-hide">
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <LocalShippingIcon sx={{ fontSize: 32, color: '#1b4332' }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1b4332' }}>Load List Found</Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                    Container: {container} | {data.mainRows.length} pallet{data.mainRows.length !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
                <Button variant="contained" size="large" startIcon={<DownloadIcon />} onClick={handleExcelDownload}
                  sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#2e7d32', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#1b5e20', boxShadow: 'none' } }}>
                  Excel
                </Button>
                <Button variant="contained" size="large" startIcon={<PrintIcon />} onClick={handlePrint}
                  sx={{ minWidth: { xs: '100%', sm: 160 }, borderRadius: 1, fontSize: '0.95rem', fontWeight: 500, background: '#1b4332', color: 'white', textTransform: 'none', boxShadow: 'none', '&:hover': { background: '#0f2419', boxShadow: 'none' } }}>
                  Print
                </Button>
              </Box>
            </Box>

            {/* Column Checkboxes */}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: '#1b4332' }}>Select Columns:</Typography>
              <FormGroup row>
                {[
                  { key: 'palletNo',     label: 'Pallet No.' },
                  { key: 'quality',      label: 'Quality' },
                  { key: 'species',      label: 'Species' },
                  { key: 'glue',         label: 'Glue' },
                  { key: 'class',        label: 'Class' },
                  { key: 'type',         label: 'Type' },
                  { key: 'certificates', label: 'Certificates' },
                ].map(({ key, label }) => (
                  <FormControlLabel key={key}
                    control={<Checkbox checked={columnVisibility[key]} onChange={() => handleColumnToggle(key)} size="small" sx={{ color: '#1b4332', '&.Mui-checked': { color: '#1b4332' } }} />}
                    label={label} sx={{ mr: 2 }}
                  />
                ))}
              </FormGroup>
            </Box>
          </CardContent>
        </Card>
      )}

      {loading && (
        <Box textAlign="center" py={8} className="print-hide">
          <CircularProgress size={50} sx={{ color: '#1b4332' }} />
          <Typography variant="body1" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>Searching container...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 1, border: '1px solid #f44336' }} className="print-hide">{error}</Alert>
      )}

      {/* Preview */}
      {data && (
        <Card className="print-hide" sx={{ mb: 4, borderRadius: 2, background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: '1rem', fontWeight: 600, textAlign: 'center', mb: 1.5 }}>
                CONTAINER LOADING PLAN
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
                CONTAINER NUMBER : {data.containerInfo?.ContainerNo || ''} ({data.containerInfo?.Shipping_Line || ''})
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
                PARTY NAME / PO NO. : {data.containerInfo?.CONSIGNEE || data.workOrderInfo?.Client_Name || ''} / PO {data.containerInfo?.ORDERNO || data.workOrderInfo?.Work_OrderNo || ''}
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' }}>
                TOTAL NUMBER OF PALLETS : {data.mainRows.length} PALLETS
              </Typography>
              {showCertLine && (
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1b4332', mt: 0.5, textAlign: 'center' }}>
                  LEVEL CERTIFICATION: FSC 100%
                </Typography>
              )}
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #e0e0e0', overflowX: 'auto' }}>
              <Table sx={{ width: '100%', tableLayout: 'fixed' }} size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#ffb74d' }}>
                    <TableCell sx={{ ...hdrCell, width: columnVisibility.palletNo ? '22%' : '30%' }}>SIZE AND THICKNESS</TableCell>
                    {columnVisibility.palletNo     && <TableCell sx={{ ...hdrCell, width: 140, minWidth: 140 }}>PALLET NO.</TableCell>}
                    <TableCell sx={{ ...hdrCell, minWidth: 100 }}>NO. OF PALLETS</TableCell>
                    <TableCell sx={{ ...hdrCell, minWidth: 110 }}>PCS. PER PALLET</TableCell>
                    {columnVisibility.quality      && <TableCell sx={{ ...hdrCell, minWidth: 90 }}>QUALITY</TableCell>}
                    {columnVisibility.species      && <TableCell sx={{ ...hdrCell, minWidth: 90 }}>SPECIES</TableCell>}
                    {columnVisibility.glue         && <TableCell sx={{ ...hdrCell, minWidth: 70 }}>GLUE</TableCell>}
                    {columnVisibility.class        && <TableCell sx={{ ...hdrCell, minWidth: 80 }}>CLASS</TableCell>}
                    {columnVisibility.type         && <TableCell sx={{ ...hdrCell, minWidth: 90 }}>TYPE</TableCell>}
                    {columnVisibility.certificates && <TableCell sx={{ ...hdrCell, minWidth: 110 }}>CERTIFICATES</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getLoadListData(data.mainRows).map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={dataCell}>{row.length} X {row.width} X {row.thickness} MM</TableCell>
                      {columnVisibility.palletNo     && <TableCell sx={dataCell}></TableCell>}
                      <TableCell sx={dataCell}>{row.palletNumber}</TableCell>
                      <TableCell sx={dataCell}>{row.pcs}</TableCell>
                      {columnVisibility.quality      && <TableCell sx={dataCell}>{row.quality || ''}</TableCell>}
                      {columnVisibility.species      && <TableCell sx={dataCell}>{row.species || ''}</TableCell>}
                      {columnVisibility.glue         && <TableCell sx={dataCell}>{row.glue || ''}</TableCell>}
                      {columnVisibility.class        && <TableCell sx={dataCell}>{row.class || ''}</TableCell>}
                      {columnVisibility.type         && <TableCell sx={dataCell}>{row.type || ''}</TableCell>}
                      {columnVisibility.certificates && <TableCell sx={dataCell}>{row.certificates || ''}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!data && !loading && !error && (
        <Card sx={{ p: 8, textAlign: 'center', borderRadius: 2, background: '#fafafa', boxShadow: 'none', border: '1px dashed #e0e0e0' }} className="print-hide">
          <LocalShippingIcon sx={{ fontSize: 60, color: '#bdbdbd', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 500 }}>No data to display</Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>Enter a container number above to get started</Typography>
        </Card>
      )}

      {/* Hidden Print Area */}
      <div ref={printRef} className="print-only">
        {data && <LoadListTable data={data} columnVisibility={columnVisibility} />}
      </div>
    </Container>
  );
};

export default LoadListViewV2;


